import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import ListingBanner from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/components/ManageListingVisibility/components/ListingBanner';
import { COLOR_VARIANT } from 'models/Colors';
import ExclamationIcon from 'icons/Exclamation.icon';

import './BulkUploadValidationBanner.styles.scss';

type BulkUploadValidationBannerProps = {
  className?: string;
  invalidMembersCount: number;
  invalidOptionalMembersCount: number;
};

const BulkUploadValidationBanner: React.FC<BulkUploadValidationBannerProps> = ({
  className,
  invalidMembersCount,
  invalidOptionalMembersCount,
}) => {
  const { t } = useTranslation();

  const classes = classNames('bulk-upload-validation-banner', className);

  const hasRequiredFieldErrors = useMemo(
    () => invalidMembersCount > 0,
    [invalidMembersCount],
  );

  const hasOptionalFieldWarnings = useMemo(
    () => !hasRequiredFieldErrors && invalidOptionalMembersCount > 0,
    [hasRequiredFieldErrors, invalidOptionalMembersCount],
  );

  const icon = useMemo(() => {
    if (hasRequiredFieldErrors) return ExclamationIcon;

    if (hasOptionalFieldWarnings) return ExclamationIcon;

    return CheckcircleIcon;
  }, [hasOptionalFieldWarnings, hasRequiredFieldErrors]);

  const color = useMemo(() => {
    if (hasRequiredFieldErrors) return COLOR_VARIANT.RED;

    if (hasOptionalFieldWarnings) return COLOR_VARIANT.ORANGE;

    return COLOR_VARIANT.GREEN;
  }, [hasOptionalFieldWarnings, hasRequiredFieldErrors]);

  const title = useMemo(() => {
    if (hasRequiredFieldErrors) return t('actionRequired');

    if (hasOptionalFieldWarnings) return t('bulkUploadReviewRecommendedTitle');

    return t('bulkUploadReadyTitle');
  }, [hasOptionalFieldWarnings, hasRequiredFieldErrors, t]);

  const description = useMemo(() => {
    if (hasRequiredFieldErrors) {
      return t('bulkUploadInvalidRowsDescription', {
        count: invalidMembersCount,
      });
    }

    if (hasOptionalFieldWarnings) {
      return t('bulkUploadInvalidOptionalRowsDescription', {
        count: invalidOptionalMembersCount,
      });
    }

    return t('bulkUploadReadyDescription');
  }, [
    hasOptionalFieldWarnings,
    hasRequiredFieldErrors,
    invalidMembersCount,
    invalidOptionalMembersCount,
    t,
  ]);

  return (
    <ListingBanner
      className={classes}
      icon={icon}
      color={color}
      title={title}
      description={description}
    />
  );
};

export default BulkUploadValidationBanner;
